import React from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { AssetRopaCapabilities } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

import {
  formatProcessingActivityReviewDate,
  getDpiaBadgeClass,
  resolveApaDpiaDisplayLabel,
  getReviewStatusDotClass,
  getUniqueRowMenuItems,
} from './assetProcessingActivityReview.helpers';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

type Props = {
  capabilities: AssetRopaCapabilities;
  emptyStateContent?: ReactNodeLike;
  onAccept: (row: AssetProcessingActivityReviewRow) => void;
  onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
  onRemove?: (row: AssetProcessingActivityReviewRow) => void;
  onRequestClarification: (row: AssetProcessingActivityReviewRow) => void;
  rows: AssetProcessingActivityReviewRow[];
};

const AssetProcessingActivityReviewBoxList: React.FC<Props> = ({
  capabilities,
  emptyStateContent,
  onAccept,
  onAssessDpia,
  onOpenAdminDpiaReview,
  onOpenAdminReview,
  onOpenOwnerEditor,
  onRemove,
  onRequestClarification,
  rows,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const showActionsMenu =
    (capabilities.canOpenApaEditor && capabilities.canEditApa) ||
    capabilities.canRemoveProcessingActivity ||
    capabilities.canAcceptRow ||
    capabilities.canRequestClarificationFromList ||
    capabilities.canAssessDpia ||
    capabilities.canOpenAdminDpiaReview;

  const handleRowClick = (row: AssetProcessingActivityReviewRow) => {
    if (capabilities.canOpenAdminReviewPage) {
      onOpenAdminReview(row);
      return;
    }

    if (capabilities.canOpenApaEditor) {
      onOpenOwnerEditor(row, 'view');
    }
  };

  const handleRowKeyDown = (event: React.KeyboardEvent, row: AssetProcessingActivityReviewRow) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowClick(row);
    }
  };

  if (rows.length === 0) {
    return (
      <div
        aria-label={t(processing_activities_messages.asset_ropa.table.aria_label)}
        className={clsx(styles.processingActivityReviewList, styles.processingActivityReviewEmptyState)}
        role='region'
      >
        {emptyStateContent}
      </div>
    );
  }

  return (
    <div
      aria-label={t(processing_activities_messages.asset_ropa.table.aria_label)}
      className={styles.processingActivityReviewList}
      role='region'
    >
      {rows.map(row => {
        const menuItems = showActionsMenu
          ? getUniqueRowMenuItems(
              row,
              capabilities,
              { onAccept, onAssessDpia, onOpenAdminDpiaReview, onOpenOwnerEditor, onRemove, onRequestClarification },
              t,
            )
          : [];

        return (
          <div
            key={row.id}
            aria-label={t(processing_activities_messages.asset_ropa.table.row_aria_label, { name: row.processingActivityName })}
            className={styles.processingActivityReviewBox}
            onClick={() => handleRowClick(row)}
            onKeyDown={event => handleRowKeyDown(event, row)}
            role='button'
            tabIndex={0}
          >
            <div className={styles.processingActivityReviewBoxHeader}>
              <div className={styles.processingActivityReviewBoxName}>{row.processingActivityName}</div>
              {menuItems.length > 0 && (
                <div
                  className={styles.processingActivityReviewBoxMenu}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                  role='presentation'
                >
                  <ContextMenu elements={menuItems}>
                    <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.row_menu)} size='small'>
                      <MoreIcon />
                    </IconButton>
                  </ContextMenu>
                </div>
              )}
            </div>
            <div className={styles.workspaceSummaryGrid}>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.review_status)}</div>
                <div className={clsx(styles.summaryValue, styles.statusCell)}>
                  <span className={clsx(styles.statusDot, getReviewStatusDotClass(row.reviewStatus, styles))} />
                  {t(processing_activities_messages.asset_ropa.review_status[row.reviewStatus as ReviewStatusMessageKey])}
                </div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.active_purposes)}</div>
                <div className={styles.summaryValue}>{row.activePurposesCount}</div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.dpia_status)}</div>
                <div className={styles.summaryValue}>
                  <span className={clsx(styles.dpiaBadge, getDpiaBadgeClass(row.dpiaDisplayStatus, styles))}>
                    {resolveApaDpiaDisplayLabel(row.dpiaDisplayStatus, t, { dpiaRequestStatus: row.dpiaRequestStatus })}
                  </span>
                </div>
              </div>
              <div>
                <div className={styles.summaryLabel}>{t(processing_activities_messages.asset_ropa.table.last_updated)}</div>
                <div className={styles.summaryValue}>{formatProcessingActivityReviewDate(row.updatedAt)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssetProcessingActivityReviewBoxList;
